// ---------------------------------------- Building control quotation
/* Building inspection company list */
export interface BuildingInspectionCompany {
    id: number;
    country_id: number;
    company_name: string;
    email: string;
    reg_no: string | null;
    landline_no: string | null;
    address: string | null;
    post_code: string | null;
    city: string | null;
    status: 1 | 0;
}

export const mapBuildingInspectionCompany = (data: any): BuildingInspectionCompany => {
    if (!data) {
        throw new Error("Invalid building inspection company type");
    }

    const buildingInspectionCompany: BuildingInspectionCompany = {
        id: data.id,
        country_id: data.country_id,
        company_name: data.company_name,
        email: data.email,
        reg_no: data.reg_no ?? null,
        landline_no: data.landline_no ?? null,
        address: data.address ?? null,
        post_code: data.post_code ?? null,
        city: data.city ?? null,
        status: data.status,
    };

    return buildingInspectionCompany;
};

export const mapBuildingInspectionCompanies = (data: any[]): BuildingInspectionCompany[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building inspection companies data");
    }

    return data.map(mapBuildingInspectionCompany);
};


/* Building control project */
export interface BuildingControlProject {
    project_id: number;
    job_number: string;
    project_type: string;
    description: string | null;
    address: string | null;
    post_code: string | null;
    full_address: string | null;
}

export const mapBuildingControlProject = (data: any): BuildingControlProject => {
    if (!data) {
        throw new Error("Invalid building control project type");
    }

    const buildingControlProject: BuildingControlProject = {
        project_id: data.project_id,
        job_number: data.job_number,
        project_type: data.project_type,
        description: data.description ?? null,
        address: data.address ?? null,
        post_code: data.post_code ?? null,
        full_address: data.full_address ?? null,
    };

    return buildingControlProject;
};


/* Building control quotation */
export interface BuildingControlQuotation {
    id: number;
    project_id: number;
    project_name: string;
    project_address: string;
    post_code: string | null;
    city: string | null;
    status: 'Pending' | 'Finished';
    area_id: number | null;
    project_detail: BuildingControlProject | null;
    quotation_share_list: BuildingControlQuotationShareList[];
}

export const mapBuildingControlQuotation = (data: any): BuildingControlQuotation => {
    if (!data) {
        throw new Error("Invalid building control quotation type");
    }

    const buildingControlQuotation: BuildingControlQuotation = {
        id: data.id,
        project_id: data.project_id,
        project_name: data.project_name,
        project_address: data.project_address,
        post_code: data.post_code ?? null,
        city: data.city ?? null,
        status: data.status,
        area_id: data.area_id ?? null,
        project_detail: data.project_detail ? mapBuildingControlProject(data.project_detail) : null,
        quotation_share_list: Array.isArray(data.quotation_share_list) ? mapBuildingControlQuotationShareLists(data.quotation_share_list) : [],
    };

    return buildingControlQuotation;
};

export const mapBuildingControlQuotations = (data: any[]): BuildingControlQuotation[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building control quotation data");
    }

    return data.map(mapBuildingControlQuotation);
};

/* Building control quotation share list */
export interface BuildingControlQuotationShareList {
    id: number;
    bc_quotation_id: number;
    company_id: number;
    response_sended: 1 | 0;
    is_approved: 1 | 0;
    responce_data_list: BuildingControlQuotationResponseField[];
    file_list: BuildingControlQuotationResponseFile[];
    respone_data: BuildingControlQuotationRequestData | null;
    company: BuildingInspectionCompany | null;
}

export const mapBuildingControlQuotationShareList = (data: any): BuildingControlQuotationShareList => {
    if (!data) {
        throw new Error("Invalid building control quotation share list type");
    }

    const buildingControlQuotationShareList: BuildingControlQuotationShareList = {
        id: data.id,
        bc_quotation_id: data.bc_quotation_id,
        company_id: data.company_id,
        response_sended: data.response_sended,
        is_approved: data.is_approved,
        responce_data_list: Array.isArray(data.responce_data_list) ? mapBuildingControlQuotationResponseFields(data.responce_data_list) : [],
        file_list: Array.isArray(data.file_list) ? mapBuildingControlQuotationResponseFiles(data.file_list) : [],
        respone_data: data.respone_data ? mapBuildingControlQuotationRequestData(data.respone_data) : null,
        company: data.company ? mapBuildingInspectionCompany(data.company) : null,
    };

    return buildingControlQuotationShareList;
};

export const mapBuildingControlQuotationShareLists = (data: any[]): BuildingControlQuotationShareList[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building control quotation share list data");
    }

    return data.map(mapBuildingControlQuotationShareList);
};

/* Building control quotation request */
export interface BuildingControlQuotationRequest {
    id: number;
    project_id: number;
    project_name: string;
    project_address: string;
    post_code: string | null;
    city: string | null;
    status: string;
    area_id: number | null;
    is_approved: 1 | 0;
    responce_data: BuildingControlQuotationRequestData | null;
    responce_data_list: BuildingControlQuotationResponseField[];
    file_list: BuildingControlQuotationResponseFile[];
    project_detail: BuildingControlProject | null;
}

export const mapBuildingControlQuotationRequest = (data: any): BuildingControlQuotationRequest => {
    if (!data) {
        throw new Error("Invalid building control quotation request type");
    }

    const buildingControlQuotationRequest: BuildingControlQuotationRequest = {
        id: data.id,
        project_id: data.project_id,
        project_name: data.project_name,
        project_address: data.project_address,
        post_code: data.post_code ?? null,
        city: data.city ?? null,
        status: data.status,
        area_id: data.area_id ?? null,
        is_approved: data.is_approved,
        responce_data: data.responce_data ? mapBuildingControlQuotationRequestData(data.responce_data) : null,
        responce_data_list: Array.isArray(data.responce_data_list) ? mapBuildingControlQuotationResponseFields(data.responce_data_list) : [],
        file_list: Array.isArray(data.file_list) ? mapBuildingControlQuotationResponseFiles(data.file_list) : [],
        project_detail: data.project_detail ? mapBuildingControlProject(data.project_detail) : null,
    };

    return buildingControlQuotationRequest;
};

export const mapBuildingControlQuotationRequests = (data: any[]): BuildingControlQuotationRequest[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building control quotation request data");
    }

    return data.map(mapBuildingControlQuotationRequest);
};

/* Building control quotation request data */
export interface BuildingControlQuotationRequestData {
    id: number;
    bc_quotation_id: number;
    company_id: number;
    status: 1 | 0;
    sign_report: 1 | 0;
    sign: string | null;
    sign_date: string | null;
    sign_report_by: number | null;
    responce_added_arc: 1 | 0;
    arc_by: number | null;
}

export const mapBuildingControlQuotationRequestData = (data: any): BuildingControlQuotationRequestData => {
    if (!data) {
        throw new Error("Invalid building control quotation request data type");
    }

    const buildingControlQuotationRequestData: BuildingControlQuotationRequestData = {
        id: data.id,
        bc_quotation_id: data.bc_quotation_id,
        company_id: data.company_id,
        status: data.status,
        sign_report: data.sign_report,
        sign: data.sign ?? null,
        sign_date: data.sign_date ?? null,
        sign_report_by: data.sign_report_by ?? null,
        responce_added_arc: data.responce_added_arc,
        arc_by: data.arc_by ?? null,
    };

    return buildingControlQuotationRequestData;
};

export const mapBuildingControlQuotationRequestDatas = (data: any[]): BuildingControlQuotationRequestData[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building control quotation request data");
    }

    return data.map(mapBuildingControlQuotationRequestData);
};

/* Building control quotation response fields*/
export interface BuildingControlQuotationResponseField {
    id: number;
    order_number: number;
    title: string;
    answer_type: 'string' | 'number';
    is_requireded: 1 | 0;
    status: 1 | 0;
    responce: BuildingControlQuotationResponseValue | null;
}

export const mapBuildingControlQuotationResponseField = (data: any): BuildingControlQuotationResponseField => {
    if (!data) {
        throw new Error("Invalid building control quotation response field type");
    }

    const buildingControlQuotationResponseField: BuildingControlQuotationResponseField = {
        id: data.id,
        order_number: data.order_number,
        title: data.title,
        answer_type: data.answer_type,
        is_requireded: data.is_requireded,
        status: data.status,
        responce: data.responce ? mapBuildingControlQuotationResponseValue(data.responce) : null,
    };

    return buildingControlQuotationResponseField;
};

export const mapBuildingControlQuotationResponseFields = (data: any[]): BuildingControlQuotationResponseField[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building control quotation response field data");
    }

    return data.map(mapBuildingControlQuotationResponseField);
};


/* Building control quotation response value */
export interface BuildingControlQuotationResponseValue {
    id: number;
    quotation_responce: number;
    responce_list_id: number;
    reply: string | number | null;
}

export const mapBuildingControlQuotationResponseValue = (data: any): BuildingControlQuotationResponseValue => {
    if (!data) {
        throw new Error("Invalid building control quotation response value type");
    }

    const buildingControlQuotationResponseValue: BuildingControlQuotationResponseValue = {
        id: data.id,
        quotation_responce: data.quotation_responce,
        responce_list_id: data.responce_list_id,
        reply: data.reply ?? null,
    };

    return buildingControlQuotationResponseValue;
};

export const mapBuildingControlQuotationResponseValues = (data: any[]): BuildingControlQuotationResponseValue[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building control quotation response value data");
    }

    return data.map(mapBuildingControlQuotationResponseValue);
};


/* Building control quotation response file*/
export interface BuildingControlQuotationResponseFile {
    id: number;
    quotation_responce: number;
    extension: string;
    file_path: string;
}

export const mapBuildingControlQuotationResponseFile = (data: any): BuildingControlQuotationResponseFile => {
    if (!data) {
        throw new Error("Invalid building control quotation response file type");
    }

    const buildingControlQuotationResponseFile: BuildingControlQuotationResponseFile = {
        id: data.id,
        quotation_responce: data.quotation_responce,
        extension: data.extension,
        file_path: data.file_path,
    };

    return buildingControlQuotationResponseFile;
};

export const mapBuildingControlQuotationResponseFiles = (data: any[]): BuildingControlQuotationResponseFile[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building control quotation response file data");
    }

    return data.map(mapBuildingControlQuotationResponseFile);
};


/* ********************************************** Building control quotation comparison */
/* Building control quotation comparison list */
export interface BuildingControlQuotationComparison {
    id: number;
    order_number: number;
    title: string;
    answer_type: 'string' | 'number';
    is_requireded: 1 | 0;
    status: 1 | 0;
    comparison_data: BuildingControlQuotationComparisonData[];
}

export const mapBuildingControlQuotationComparison = (data: any): BuildingControlQuotationComparison => {
    if (!data) {
        throw new Error("Invalid building control quotation comparison type");
    }

    const buildingControlQuotationComparison: BuildingControlQuotationComparison = {
        id: data.id,
        order_number: data.order_number,
        title: data.title,
        answer_type: data.answer_type,
        is_requireded: data.is_requireded,
        status: data.status,
        comparison_data: Array.isArray(data.comparison_data) ? mapBuildingControlQuotationComparisonDatas(data.comparison_data) : [],
    };

    return buildingControlQuotationComparison;
};

export const mapBuildingControlQuotationComparisons = (data: any[]): BuildingControlQuotationComparison[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building control quotation comparison data");
    }

    return data.map(mapBuildingControlQuotationComparison);
};


/* Building control quotation comparison data */
export interface BuildingControlQuotationComparisonData {
    company_id: number;
    reply: string | number | null;
    is_highlight: boolean;
    is_min: boolean | null;
}

export const mapBuildingControlQuotationComparisonData = (data: any): BuildingControlQuotationComparisonData => {
    if (!data) {
        throw new Error("Invalid building control quotation comparison data type");
    }

    const buildingControlQuotationComparisonData: BuildingControlQuotationComparisonData = {
        company_id: data.company_id,
        reply: data.reply ?? null,
        is_highlight: data.is_highlight,
        is_min: data.is_min ?? null,
    };

    return buildingControlQuotationComparisonData;
};

export const mapBuildingControlQuotationComparisonDatas = (data: any[]): BuildingControlQuotationComparisonData[] => {
    if (!Array.isArray(data)) {
        throw new Error("Invalid building control quotation comparison data");
    }

    return data.map(mapBuildingControlQuotationComparisonData);
};
/* ********************************************** */

/* Response Payload */
export interface BuildingControlQuotationResponsePayload {
    responce_list_id: number;
    reply: string | number | null;
}